import { GeoLocationContext } from "./GeoLocationContext"
import {
  useUserLocation,
  useDeviceLocation as useBrowserDeviceLocation,
} from "hooks"
import config from "config"
import { useLocationHistory } from "./hooks"
import { useCallback, useMemo } from "react"
import { useVenue } from "providers/venue"
import _ from "lodash"
import { prepareGeolocationFeature } from "./utils/prepareGeolocationFeature.ts"
import { useElectronDeviceLocation } from "hooks/electron"
import { DEFAULT_VENUE_FILTER } from "constant"

const userLocationStaleTime = config("userLocationStaleTime")
const isKioskMode = ["kiosk-v", "kiosk-h"].includes(config("device"))

const useDeviceLocation =
  config("appType") === "webapp"
    ? useBrowserDeviceLocation
    : useElectronDeviceLocation

export const GeoLocationProvider = ({ children }) => {
  const { levels, venues, units, sections, dataLoaded, isMultiVenueProject } =
    useVenue()
  const { deviceLocation, deviceLocationName } = useDeviceLocation()
  const { scannedLocations: scannedLocationsState, addScannedLocationHistory } =
    useLocationHistory()
  const { isLoading, userLocation: userLocationState } = useUserLocation(
    deviceLocation,
    {
      onUpdate: addScannedLocationHistory,
      staleTime: userLocationStaleTime,
      update: !isKioskMode,
      enable: dataLoaded,
    }
  )
  const userLocation = useMemo(
    () =>
      prepareGeolocationFeature(userLocationState, {
        venues,
        levels,
        units,
        sections,
      }),
    [userLocationState, venues, levels, units, sections]
  )

  const locationVenue = useMemo(() => {
    return _.get(userLocation, "properties.venue", { id: DEFAULT_VENUE_FILTER })
  }, [userLocation])

  const locationOrdinal = useMemo(() => {
    return _.get(userLocation, "properties.ordinal", 0)
  }, [userLocation])

  const locationSection = useMemo(() => {
    return _.get(userLocation, "properties.section", null)
  }, [userLocation])

  const userLocationFloorName = useMemo(() => {
    if (locationOrdinal === null || locationOrdinal === undefined) return null
    if (!locationVenue?.id) return null

    const sortedLevels = _.orderBy(levels, "properties.ordinal", "desc")

    const locationVenueLevels = isMultiVenueProject
      ? _(sortedLevels)
          .filter(
            (level) => _.get(level, "properties.venue_id") === locationVenue.id
          )
          .uniqBy("properties.name.en")
          .value()
      : sortedLevels

    if (!locationVenueLevels || locationVenueLevels.length === 0) return null

    const level = locationVenueLevels.find(
      (level) => level.properties?.ordinal === locationOrdinal
    )

    return level?.properties?.name?.en || null
  }, [locationOrdinal, locationVenue, levels, isMultiVenueProject])

  const scannedLocations = useMemo(
    () =>
      scannedLocationsState.map((location) =>
        prepareGeolocationFeature(location, { venues, levels, units, sections })
      ),
    [scannedLocationsState, levels, venues, units, sections]
  )

  const findGeoLocationFeatureById = useCallback(
    (id) =>
      _.compact([userLocation, ...scannedLocations]).find(
        ({ id: geoLocationId }) => geoLocationId === id
      ),
    [userLocation, scannedLocations]
  )

  const value = {
    isLoading,
    userLocation,
    scannedLocations,
    deviceLocation,
    deviceLocationName,
    locationVenue,
    locationOrdinal,
    locationSection,
    userLocationFloorName,
    // Function
    findGeoLocationFeatureById,
  }

  return (
    <GeoLocationContext.Provider value={value}>
      {children}
    </GeoLocationContext.Provider>
  )
}
